import React, { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

function Section({ title, children, onSave, testId }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-navy">{title}</h3>
        <Button size="sm" data-testid={`save-${testId}`} onClick={onSave} className="bg-brandblue font-display font-semibold hover:bg-blue-700"><Save className="mr-1 h-4 w-4" />Save</Button>
      </div>
      <div className="mt-5 space-y-4">{children}</div>
    </div>
  );
}

export default function AdminContent() {
  const [content, setContent] = useState({});
  const upd = (key, value) => setContent((c) => ({ ...c, [key]: value }));

  useEffect(() => { api.get("/site-content").then((r) => setContent(r.data || {})).catch(() => {}); }, []);

  const save = async (key) => {
    try { await api.put(`/admin/site-content/${key}`, { value: content[key] }); toast.success("Saved."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const intro = content.memberships_intro || {};
  const pricing = content.experience_pricing || {};
  const contact = content.contact_info || {};
  const steps = content.step_up_steps || [];
  const faq = content.step_up_faq || [];

  return (
    <AppShell admin>
      <h1 className="font-display text-3xl font-bold text-navy">Site Content</h1>
      <p className="text-slateBrand">Edit marketing copy, pricing, booking steps and FAQ without touching code.</p>

      <div className="mt-8 space-y-6">
        <Section title="Memberships Intro" testId="intro" onSave={() => save("memberships_intro")}>
          <div><Label>Headline</Label><Input data-testid="content-intro-headline" className="mt-1.5 h-11" value={intro.headline || ""} onChange={(e) => upd("memberships_intro", { ...intro, headline: e.target.value })} /></div>
          <div><Label>Subtext</Label><Textarea className="mt-1.5" value={intro.subtext || ""} onChange={(e) => upd("memberships_intro", { ...intro, subtext: e.target.value })} /></div>
        </Section>

        <Section title="Experience Pricing" testId="pricing" onSave={() => save("experience_pricing")}>
          <div className="grid grid-cols-3 gap-4">
            <div><Label>Day Explorer $</Label><Input data-testid="content-day-explorer" type="number" className="mt-1.5 h-11" value={pricing.day_explorer || ""} onChange={(e) => upd("experience_pricing", { ...pricing, day_explorer: Number(e.target.value) })} /></div>
            <div><Label>Signature Member $</Label><Input type="number" className="mt-1.5 h-11" value={pricing.signature_member || ""} onChange={(e) => upd("experience_pricing", { ...pricing, signature_member: Number(e.target.value) })} /></div>
            <div><Label>Signature Guest $</Label><Input type="number" className="mt-1.5 h-11" value={pricing.signature_guest || ""} onChange={(e) => upd("experience_pricing", { ...pricing, signature_guest: Number(e.target.value) })} /></div>
          </div>
          <div><Label>Day Explorer description</Label><Textarea className="mt-1.5" value={pricing.day_explorer_desc || ""} onChange={(e) => upd("experience_pricing", { ...pricing, day_explorer_desc: e.target.value })} /></div>
          <div><Label>Signature Member description</Label><Textarea className="mt-1.5" value={pricing.signature_member_desc || ""} onChange={(e) => upd("experience_pricing", { ...pricing, signature_member_desc: e.target.value })} /></div>
          <div><Label>Signature Guest description</Label><Textarea className="mt-1.5" value={pricing.signature_guest_desc || ""} onChange={(e) => upd("experience_pricing", { ...pricing, signature_guest_desc: e.target.value })} /></div>
        </Section>

        <Section title="Contact Information" testId="contact" onSave={() => save("contact_info")}>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Email</Label><Input data-testid="content-contact-email" className="mt-1.5 h-11" value={contact.email || ""} onChange={(e) => upd("contact_info", { ...contact, email: e.target.value })} /></div>
            <div><Label>Phone</Label><Input className="mt-1.5 h-11" value={contact.phone || ""} onChange={(e) => upd("contact_info", { ...contact, phone: e.target.value })} /></div>
          </div>
          <div><Label>Address</Label><Input className="mt-1.5 h-11" value={contact.address || ""} onChange={(e) => upd("contact_info", { ...contact, address: e.target.value })} /></div>
          <div><Label>Privacy statement</Label><Textarea className="mt-1.5" value={contact.privacy || ""} onChange={(e) => upd("contact_info", { ...contact, privacy: e.target.value })} /></div>
        </Section>

        <Section title="Step Up Booking Steps" testId="steps" onSave={() => save("step_up_steps")}>
          {steps.map((s, i) => (
            <div key={i} className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <span className="font-display text-sm font-bold text-navy">Step {i + 1}</span>
                <button data-testid={`del-step-${i}`} onClick={() => upd("step_up_steps", steps.filter((_, j) => j !== i))} className="text-alertBrand"><Trash2 className="h-4 w-4" /></button>
              </div>
              <Input className="mt-2 h-10" placeholder="Title" value={s.title} onChange={(e) => upd("step_up_steps", steps.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} />
              <Textarea className="mt-2" placeholder="Body" value={s.body} onChange={(e) => upd("step_up_steps", steps.map((x, j) => j === i ? { ...x, body: e.target.value } : x))} />
            </div>
          ))}
          <Button size="sm" variant="outline" data-testid="add-step" onClick={() => upd("step_up_steps", [...steps, { title: "", body: "" }])}><Plus className="mr-1 h-4 w-4" />Add Step</Button>
        </Section>

        <Section title="Step Up FAQ" testId="faq" onSave={() => save("step_up_faq")}>
          {faq.map((f, i) => (
            <div key={i} className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <span className="font-display text-sm font-bold text-navy">Q{i + 1}</span>
                <button data-testid={`del-faq-${i}`} onClick={() => upd("step_up_faq", faq.filter((_, j) => j !== i))} className="text-alertBrand"><Trash2 className="h-4 w-4" /></button>
              </div>
              <Input className="mt-2 h-10" placeholder="Question" value={f.q} onChange={(e) => upd("step_up_faq", faq.map((x, j) => j === i ? { ...x, q: e.target.value } : x))} />
              <Textarea className="mt-2" placeholder="Answer" value={f.a} onChange={(e) => upd("step_up_faq", faq.map((x, j) => j === i ? { ...x, a: e.target.value } : x))} />
            </div>
          ))}
          <Button size="sm" variant="outline" data-testid="add-faq" onClick={() => upd("step_up_faq", [...faq, { q: "", a: "" }])}><Plus className="mr-1 h-4 w-4" />Add FAQ</Button>
        </Section>
      </div>
    </AppShell>
  );
}
